import * as React from 'react';
import { BaSeBreakpointContext } from '../../contexts/breakpoint';
import { BaSeI18nContext } from '../../contexts/i18n';
import { MessageKey } from '../../i18n';
import { BaSeTheme } from '../../theme';
import { ThemeColorValue } from '../../theme/theme-interface';
import { BaSeTag, TagProps } from '../labels/tag/tag';
import {
  StepContentDefinition,
  StepperContentToRender,
} from '../stepper/step-content';
import { useStepperTransition } from '../stepper/use-stepper-transition';
import { BaSeParagraph } from '../typography/paragraph/paragraph';
import { BaSeSmall1 } from '../typography/small/small1';
import Styled from './compact-stepper-styled';

const MAXIMUM_STEPS = 10;

type CompactStepState =
  | 'active'
  | 'activeError'
  | 'completed'
  | 'completedError'
  | 'disabled'
  | 'uncompleted'
  | 'uncompletedError';

const stepMessageKeys: Record<CompactStepState, MessageKey> = {
  active: 'compactStepper.step.active',
  activeError: 'compactStepper.step.activeError',
  completed: 'compactStepper.step.completed',
  completedError: 'compactStepper.step.completedError',
  disabled: 'compactStepper.step.disabled',
  uncompleted: 'compactStepper.step.uncompleted',
  uncompletedError: 'compactStepper.step.uncompletedError',
};

export type CompactSegmentSize = 'small' | 'medium' | 'large';
export type CompactTitleMaxLines = 1 | 2 | 3;
export type CompactFocusRingMode = 'all' | 'keyboard-only';

export interface CompactStepProps extends StepContentDefinition {
  title: string;
  errorTagProps?: Partial<TagProps>;
  isClickable?: boolean;
  isCompleted?: boolean;
  withError?: boolean;
}

export interface CompactStepperProps {
  steps: CompactStepProps[];
  currentStep: number;
  onStepChange: (step: number) => void;
  navigationAriaLabel?: string;
  showErrorLabelOnMobile?: boolean;
  segmentSize?: CompactSegmentSize;
  titleMaxLines?: CompactTitleMaxLines;
  withTransition?: boolean;
  preserveContent?: boolean;
  focusRingMode?: CompactFocusRingMode;
  activeColor?: ThemeColorValue;
  completedColor?: ThemeColorValue;
  uncompletedColor?: ThemeColorValue;
  activeWithErrorColor?: ThemeColorValue;
  completedWithErrorColor?: ThemeColorValue;
  uncompletedWithErrorColor?: ThemeColorValue;
}

interface ResolvedCompactColors {
  active: ThemeColorValue;
  activeError: ThemeColorValue;
  completed: ThemeColorValue;
  completedError: ThemeColorValue;
  disabled: ThemeColorValue;
  uncompleted: ThemeColorValue;
  uncompletedError: ThemeColorValue;
}

interface CompactStepSegmentProps {
  accessibleLabel: string;
  color: ThemeColorValue;
  isActive: boolean;
  isClickable: boolean;
  isDisabled: boolean;
  onActivated: () => void;
  segmentSize: CompactSegmentSize;
  focusRingMode: CompactFocusRingMode;
}

function getStepState(
  step: CompactStepProps,
  stepNumber: number,
  currentStep: number,
): CompactStepState {
  if (step.isDisabled) {
    return 'disabled';
  }

  const isActive = stepNumber === currentStep;
  const isCompleted = step.isCompleted ?? currentStep > stepNumber;

  if (step.withError) {
    if (isCompleted) {
      return 'completedError';
    }

    if (isActive) {
      return 'activeError';
    }

    return 'uncompletedError';
  }

  if (isActive) {
    return 'active';
  }

  if (isCompleted) {
    return 'completed';
  }

  return 'uncompleted';
}

function getStepMessageKey(state: CompactStepState): MessageKey {
  return stepMessageKeys[state];
}

const CompactStepSegment: React.FC<CompactStepSegmentProps> = ({
  accessibleLabel,
  color,
  isActive,
  isClickable,
  isDisabled,
  onActivated,
  segmentSize,
  focusRingMode,
}) => (
  <Styled.CompactStep>
    <Styled.CompactStepButton
      type="button"
      $color={color}
      $focusRingMode={focusRingMode}
      $isClickable={isClickable}
      aria-current={isActive ? 'step' : undefined}
      aria-disabled={!isClickable || isDisabled}
      aria-label={accessibleLabel}
      disabled={isDisabled}
      tabIndex={isClickable && !isDisabled ? 0 : -1}
      onClick={isClickable && !isDisabled ? onActivated : undefined}
    >
      <Styled.CompactSegment $color={color} $size={segmentSize} />
    </Styled.CompactStepButton>
  </Styled.CompactStep>
);

export const BaSeCompactStepper: React.FC<CompactStepperProps> = ({
  steps,
  currentStep,
  onStepChange,
  navigationAriaLabel,
  showErrorLabelOnMobile = true,
  segmentSize = 'medium',
  titleMaxLines = 2,
  withTransition = false,
  preserveContent = false,
  focusRingMode = 'all',
  activeColor = BaSeTheme.colors.institucionais.azulSebrae36,
  completedColor = BaSeTheme.colors.feedbackInterface.sucesso70,
  uncompletedColor = BaSeTheme.colors.institucionais.cinzaSebrae90,
  activeWithErrorColor = BaSeTheme.colors.feedbackInterface.atencao35,
  completedWithErrorColor = BaSeTheme.colors.feedbackInterface.erro45,
  uncompletedWithErrorColor = BaSeTheme.colors.feedbackInterface.atencao55,
}) => {
  const { onMobileSmall, onMobile, onMobileLarge } = React.useContext(
    BaSeBreakpointContext,
  );
  const { getMessage } = React.useContext(BaSeI18nContext);
  const transition = useStepperTransition(currentStep, withTransition);

  if (!Array.isArray(steps) || steps.length === 0) {
    throw Error('Prop "steps" é obrigatória');
  }

  if (steps.length > MAXIMUM_STEPS) {
    throw Error('Prop "steps" deve conter entre 1 e 10 etapas');
  }

  if (steps.some(({ title }) => typeof title !== 'string' || !title.trim())) {
    throw Error('Todas as etapas devem possuir um "title"');
  }

  if (
    !Number.isInteger(currentStep) ||
    currentStep < 1 ||
    currentStep > steps.length
  ) {
    throw Error(
      `Prop "currentStep" deve ser um número inteiro entre 1 e ${steps.length}`,
    );
  }

  if (!onStepChange) {
    throw Error('Prop "onStepChange" é obrigatória');
  }

  const colors: ResolvedCompactColors = {
    active: activeColor,
    activeError: activeWithErrorColor,
    completed: completedColor,
    completedError: completedWithErrorColor,
    disabled: BaSeTheme.colors.institucionais.cinzaSebrae75,
    uncompleted: uncompletedColor,
    uncompletedError: uncompletedWithErrorColor,
  };
  const activeStep = steps[currentStep - 1];
  const activeState = getStepState(activeStep, currentStep, currentStep);
  let activeTitleColor = BaSeTheme.colors.institucionais.cinzaSebrae20;
  if (activeState === 'activeError' || activeState === 'completedError') {
    activeTitleColor = colors[activeState];
  } else if (activeState === 'disabled') {
    activeTitleColor = colors.disabled;
  }

  const {
    color: customErrorTagColor,
    label: customErrorTagLabel,
    size: customErrorTagSize,
    type: customErrorTagType,
    ...customErrorTagProps
  } = activeStep.errorTagProps ?? {};
  let errorTag: TagProps | undefined;
  if (activeState === 'activeError') {
    errorTag = {
      ...customErrorTagProps,
      color: customErrorTagColor ?? activeWithErrorColor,
      label:
        customErrorTagLabel ?? getMessage('compactStepper.tag.incompleteError'),
      size: customErrorTagSize ?? 'small',
      type: customErrorTagType ?? 'secondary',
    };
  } else if (activeState === 'completedError') {
    errorTag = {
      ...customErrorTagProps,
      color: customErrorTagColor ?? completedWithErrorColor,
      label:
        customErrorTagLabel ?? getMessage('compactStepper.tag.completedError'),
      size: customErrorTagSize ?? 'small',
      type: customErrorTagType ?? 'secondary',
    };
  }
  const counter = getMessage(
    'compactStepper.counter',
    currentStep,
    steps.length,
  );
  const isMobile = onMobileSmall || onMobile || onMobileLarge;

  return (
    <Styled.CompactStepperContainer>
      <Styled.CompactNavigation
        aria-label={
          navigationAriaLabel?.trim() ?? getMessage('compactStepper.navigation')
        }
      >
        <Styled.CompactHeader>
          <Styled.CompactTitle>
            <Styled.CompactTitleText $maxLines={titleMaxLines}>
              {isMobile ? (
                <BaSeSmall1 color={activeTitleColor}>
                  {activeStep.title}
                </BaSeSmall1>
              ) : (
                <BaSeParagraph color={activeTitleColor}>
                  {activeStep.title}
                </BaSeParagraph>
              )}
            </Styled.CompactTitleText>
            {errorTag && (!isMobile || showErrorLabelOnMobile) && (
              <BaSeTag {...errorTag} />
            )}
          </Styled.CompactTitle>

          <Styled.CompactCounter aria-live="polite">
            {isMobile ? (
              <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae60}>
                {counter}
              </BaSeSmall1>
            ) : (
              <BaSeParagraph
                color={BaSeTheme.colors.institucionais.cinzaSebrae60}
              >
                {counter}
              </BaSeParagraph>
            )}
          </Styled.CompactCounter>
        </Styled.CompactHeader>

        <Styled.CompactSteps>
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            const state = getStepState(step, stepNumber, currentStep);

            return (
              <CompactStepSegment
                key={stepNumber}
                accessibleLabel={getMessage(
                  getStepMessageKey(state),
                  stepNumber,
                  steps.length,
                  step.title,
                )}
                color={colors[state]}
                isActive={stepNumber === currentStep}
                isClickable={step.isClickable ?? true}
                isDisabled={step.isDisabled ?? false}
                onActivated={() => onStepChange(stepNumber)}
                segmentSize={segmentSize}
                focusRingMode={focusRingMode}
              />
            );
          })}
        </Styled.CompactSteps>
      </Styled.CompactNavigation>

      <StepperContentToRender
        preserveContent={preserveContent}
        orientation="horizontal"
        transition={transition}
        steps={steps}
        currentStep={currentStep}
        onStepChange={onStepChange}
      />
    </Styled.CompactStepperContainer>
  );
};

BaSeCompactStepper.displayName = 'BaSeCompactStepper';
